'use strict';

import {
	BREAKPOINTS,
	isHeroFilterVisible,
	isMobile,
} from '../../utility/index.js';
import { CONFIG } from '../core/config.js';
import { PARAMETER_CONFIG } from '../core/parameter-config.js';

/**
 * Dropdown Trigger Manager
 * Handles triggering specialty, insurance, and type of care dropdown selections from external elements with data attributes
 *
 * Usage:
 * - Add data-specialty="Anxiety" to any element (case-insensitive)
 * - Add data-insurance="Humana" to any element (case-insensitive) - only works within a parent element with data-logo-banner-interstitial="true"
 * - Add data-type-of-care="Talk Therapy" to any element (case-insensitive)
 * - Clicking will select the matching option across all forms
 * - Only one selection is allowed at a time per dropdown type
 *
 * Example:
 * <span data-header-interstitial-trigger data-sticky-mobile-cta-trigger data-specialty="Anxiety">Anxiety</span>
 * <div data-logo-banner-interstitial="true">
 *   <div><svg data-insurance="Blue Cross Blue Shield"></svg></div>
 * </div>
 */
export class DropdownTriggerManager {
	constructor(forms, formSyncManager) {
		this.forms = forms;
		this.formSyncManager = formSyncManager;
		this.activeSelections = new Map(); // Track current selections per dropdown type

		// Input name + single-select behavior come from the shared dropdown registry
		// (PARAMETER_CONFIG) so they stay in sync with URLManager. Only the
		// trigger-specific fields (data attribute, selection key) are defined here.
		this.dropdownConfig = {
			specialty: {
				dataAttribute: 'data-specialty',
				inputName: PARAMETER_CONFIG.specialty.dropdownType,
				selectionKey: 'specialties',
				isSingleSelect: PARAMETER_CONFIG.specialty.singleSelect,
			},
			insurance: {
				dataAttribute: 'data-insurance',
				inputName: PARAMETER_CONFIG.insurance.dropdownType,
				selectionKey: 'payors',
				isSingleSelect: PARAMETER_CONFIG.insurance.singleSelect,
			},
			typeOfCare: {
				dataAttribute: 'data-type-of-care',
				inputName: PARAMETER_CONFIG.typeOfCare.dropdownType,
				selectionKey: 'typeOfCare',
				isSingleSelect: PARAMETER_CONFIG.typeOfCare.singleSelect,
			},
		};

		this.cachedElements = {
			hero: null,
			heroFilter: null,
			header: null,
			headerHeight: null,
		};

		this.init();
	}

	init() {
		// Wait for DOM to be fully loaded
		if (document.readyState === 'loading') {
			document.addEventListener('DOMContentLoaded', () =>
				this.bindTriggerEvents(),
			);
		} else {
			this.bindTriggerEvents();
		}
	}

	bindTriggerEvents() {
		// Use capture phase to catch events before they might be stopped
		document.addEventListener(
			'click',
			(e) => {
				// Check each dropdown type configuration
				for (const [dropdownType, config] of Object.entries(
					this.dropdownConfig,
				)) {
					const trigger = e.target.closest(`[${config.dataAttribute}]`);
					if (trigger?.dataset[dropdownType]) {
						// Check if insurance functionality should be enabled
						if (
							dropdownType === 'insurance' &&
							!this.isInsuranceEnabled(trigger)
						) {
							continue; // Skip insurance handling if not enabled
						}

						// For insurance clicks, check if we should prevent mobile CTA trigger
						if (
							dropdownType === 'insurance' &&
							this.shouldPreventMobileCta(trigger)
						) {
							this.preventMobileCtaTrigger(e);
						}

						// TODO: This is temporary, we only want this to happen on this page /start/therapy-today/
						if (
							dropdownType === 'typeOfCare' &&
							window.location.pathname === '/start/therapy-today/'
						) {
							this.handleTypeOfCareClick(e, trigger, config);
						}

						this.handleDropdownClick(
							dropdownType,
							trigger.dataset[dropdownType],
						);
						return; // Only handle one trigger per click
					}
				}
			},
			true,
		);
	}

	/**
	 * If we are in mobile
	 * We need to dispatch a custom event to the header script to open the drawer interstitial
	 * Then we need to sync the selected value to all matching radio inputs
	 *
	 * @param {Event}       e       - The event that triggered the type of care click
	 * @param {HTMLElement} trigger - The element that triggered the type of care click
	 * @param {Object}      config  - The configuration for the type of care dropdown
	 * @return {void}
	 */
	handleTypeOfCareClick(e, trigger, config) {
		if (!isMobile(BREAKPOINTS.HEADER_DESKTOP)) return;

		e.preventDefault();
		e.stopPropagation();

		trigger.dispatchEvent(
			new CustomEvent('type-of-care-mobile-click', {
				bubbles: true,
				cancelable: true,
				detail: { originalEvent: e, trigger },
			}),
		);

		const radioInput = document.querySelectorAll(
			`input[name="${config.inputName}"][type="radio"]`,
		);
		radioInput.forEach((radio) => {
			if (radio.value === trigger.dataset[config.dataAttribute]) {
				radio.checked = true;
				this.formSyncManager.syncRadioToAllForms(radio);
			}
		});
	}

	/**
	 * Clear all checked selections in a specific field across all forms
	 *
	 * @param {string} inputName - The name of the input to clear selections from
	 * @return {void}
	 */
	clearAllSelectionsInField(inputName) {
		if (!this.forms || this.forms.length === 0) return;

		this.forms.forEach((form) => {
			const checkedCheckboxes = form.querySelectorAll(
				`input[name="${inputName}"][type="checkbox"]:checked`,
			);
			checkedCheckboxes.forEach((checkbox) => {
				this.formSyncManager.syncCheckboxDeselectionToAllForms(checkbox);
			});
		});
	}

	/**
	 * Ensure single-select styling is applied across all forms
	 * Adds the selected CSS class to the option element
	 *
	 * @param {HTMLInputElement} sourceCheckbox - The checkbox input that was selected
	 * @return {void}
	 */
	ensureSingleSelectStyling(sourceCheckbox) {
		if (!this.forms || this.forms.length === 0) return;

		const { name, value } = sourceCheckbox;

		this.forms.forEach((form) => {
			const targetCheckbox = form.querySelector(
				`input[name="${name}"][value="${value}"]`,
			);
			if (targetCheckbox?.checked) {
				const option = targetCheckbox.closest(CONFIG.ELEMENTS.option);
				if (
					option &&
					targetCheckbox.matches(CONFIG.ELEMENTS.checkboxSingleSelect)
				) {
					option.classList.add(CONFIG.CSS_CLASSES.optionSelected);
				}
			}
		});
	}

	/**
	 * Get cached DOM elements
	 */
	getCachedElements() {
		if (!this.cachedElements.hero) {
			this.cachedElements.hero = document.querySelector('[data-search-hero]');
			this.cachedElements.heroFilter = this.cachedElements.hero?.querySelector(
				'[data-search-filter-form]',
			);
			this.cachedElements.header = document.querySelector('header');
			this.cachedElements.headerHeight =
				this.cachedElements.header?.offsetHeight || 0;
		}

		return this.cachedElements;
	}

	handleDropdownClick(dropdownType, value) {
		const config = this.dropdownConfig[dropdownType];
		if (!config) return;

		const targetCheckbox = this.findInput(config.inputName, value, 'checkbox');
		if (targetCheckbox) {
			this.clearPreviousSelection(config.selectionKey, config.inputName);
			this.selectOption(targetCheckbox, config.selectionKey, value);

			return;
		}

		const targetRadio = this.findInput(config.inputName, value, 'radio');
		if (!targetRadio) return;

		this.clearPreviousSelection(config.selectionKey, config.inputName);
		this.selectOption(targetRadio, config.selectionKey, value);
	}

	/**
	 * Check if insurance functionality is enabled by looking for a parent element
	 * with data-logo-banner-interstitial="true"
	 *
	 * @param {HTMLElement} triggerElement - The element that triggered the insurance functionality
	 * @return {boolean} - True if insurance functionality is enabled, false otherwise
	 */
	isInsuranceEnabled(triggerElement) {
		return (
			triggerElement.closest('[data-logo-banner-interstitial="true"]') !== null
		);
	}

	/**
	 * Check if a field is configured as single-select
	 *
	 * @param {string} selectionKey - The key of the selection to check
	 * @return {boolean} - True if the field is configured as single-select, false otherwise
	 */
	isSingleSelectField(selectionKey) {
		const config = Object.values(this.dropdownConfig).find(
			(c) => c.selectionKey === selectionKey,
		);
		return config?.isSingleSelect === true;
	}

	/**
	 * Prevent mobile CTA trigger by adding a data attribute that the header component can check
	 *
	 * @param {Event} event - The event that triggered the mobile CTA trigger
	 * @return {void}
	 */
	preventMobileCtaTrigger(event) {
		// Add a temporary data attribute to prevent mobile CTA trigger
		event.target.setAttribute('data-prevent-mobile-cta', 'true');

		// Remove the attribute after a short delay to avoid affecting other interactions
		setTimeout(() => {
			event.target.removeAttribute('data-prevent-mobile-cta');
		}, 100);
	}

	/**
	 * Check if mobile CTA should be prevented for insurance clicks
	 * Returns true if hero filters are still visible (user doesn't need sticky CTA)
	 *
	 * @param {HTMLElement} triggerElement - The element that triggered the mobile CTA trigger
	 * @return {boolean} - True if mobile CTA should be prevented, false otherwise
	 */
	shouldPreventMobileCta(triggerElement) {
		// Only prevent on mobile and if element has mobile CTA trigger attribute
		if (
			!isMobile(BREAKPOINTS.HEADER_DESKTOP) ||
			!triggerElement.hasAttribute('data-sticky-mobile-cta-trigger')
		) {
			return false;
		}

		const { heroFilter, headerHeight } = this.getCachedElements();
		return isHeroFilterVisible(heroFilter, headerHeight);
	}

	findInput(inputName, value, inputType) {
		if (!value || typeof value !== 'string') return null;

		const normalizedValue = value.toLowerCase();
		const inputs = document.querySelectorAll(
			`input[name="${inputName}"][type="${inputType}"]`,
		);

		for (const input of inputs) {
			if (input.value?.toLowerCase() === normalizedValue) {
				return input;
			}
		}
		return null;
	}

	clearPreviousSelection(selectionKey, inputName) {
		const previousValue = this.activeSelections.get(selectionKey);

		// For single-select fields, clear ALL checked options in the same field
		if (this.isSingleSelectField(selectionKey)) {
			this.clearAllSelectionsInField(inputName);
		} else if (previousValue) {
			// For multi-select fields, only clear the previously tracked selection
			const previousInput = this.findInput(
				inputName,
				previousValue,
				'checkbox',
			);
			if (previousInput?.checked) {
				this.formSyncManager.syncCheckboxDeselectionToAllForms(previousInput);
			}
		}
	}

	selectOption(checkbox, selectionKey, value) {
		if (!checkbox || !this.formSyncManager) return;

		if (
			this.isSingleSelectField(selectionKey) &&
			selectionKey === 'typeOfCare'
		) {
			checkbox.checked = true;
			this.formSyncManager.syncRadioToAllForms(checkbox);
		} else if (this.isSingleSelectField(selectionKey)) {
			checkbox.checked = true;
			this.formSyncManager.syncCheckboxToAllForms(checkbox);
			this.ensureSingleSelectStyling(checkbox);
		} else if (!checkbox.checked) {
			checkbox.checked = true;
			this.formSyncManager.syncCheckboxToAllForms(checkbox);
		}

		this.activeSelections.set(selectionKey, value);
	}

	destroy() {
		this.activeSelections.clear();
	}
}
